/**
 * copyCamera — la prise d'une page de la copie dans un navigateur, de l'ouverture à la photo gardée :
 * une machine pure.
 *
 * Au téléphone, la copie se prend au scanner (Genius Scan) ; dans un navigateur, à la caméra, par
 * cet écran. L'ancien (`CameraComponent.web.js`) laissait la caméra au composant `react-webcam`, et
 * la seconde photo d'une même activité pouvait rester noire. Ici, chaque ouverture est une **séance**
 * numérotée : le flux qu'elle demande lui appartient, une réponse qui arrive pour une séance passée
 * (une caméra fermée, retournée) est ignorée et son flux rendu, et le flux d'une séance est arrêté à sa
 * fin. Une seconde photo ouvre une seconde séance, avec son propre flux.
 *
 *  - `starting` : la caméra est demandée au navigateur ;
 *  - `ready` : le viseur montre la caméra, le déclencheur prend ;
 *  - `captured` : la photo prise attend « Reprendre » ou « Valider » ; le viseur reste ouvert dessous,
 *    « Reprendre » y revient sans redemander la caméra ;
 *  - `failed` : le navigateur a refusé (`denied`), il n'a pas de caméra (`unavailable`), une autre
 *    application la tient (`busy`), ou autre chose (`error`) — l'élève le lit ; « Réessayer » rouvre
 *    une séance, et l'écran offre d'importer une photo à la place ;
 *  - `closed` : rien d'ouvert.
 *
 * Le réducteur ne touche ni au navigateur ni à l'heure : le crochet (`useCopyCamera`) lui donne des
 * événements.
 */

export type CameraFacing = "user" | "environment"

export type CopyCameraFailure = "denied" | "unavailable" | "busy" | "error"

export type CopyCameraStatus = "closed" | "starting" | "ready" | "captured" | "failed"

export type CopyCameraState = {
    status: CopyCameraStatus
    /** La séance en cours : chaque ouverture, chaque caméra retournée, chaque « Réessayer » en ouvre une. */
    session: number
    facing: CameraFacing | null
    failure: CopyCameraFailure | null
    /** La photo prise, en adresse de données (`data:image/jpeg…`). */
    photo: string | null
}

export type CopyCameraEvent =
    | { type: "OPEN"; facing: CameraFacing | null }
    | { type: "STREAM_READY"; session: number }
    | { type: "STREAM_FAILED"; session: number; failure: CopyCameraFailure }
    | { type: "CAPTURED"; photo: string }
    | { type: "RETAKE" }
    | { type: "FLIP" }
    | { type: "RETRY" }
    | { type: "CLOSE" }

export const CLOSED_CAMERA: CopyCameraState = {
    status: "closed",
    session: 0,
    facing: null,
    failure: null,
    photo: null,
}

function newSession(state: CopyCameraState, facing: CameraFacing | null): CopyCameraState {
    return { status: "starting", session: state.session + 1, facing, failure: null, photo: null }
}

export function copyCameraReducer(state: CopyCameraState, event: CopyCameraEvent): CopyCameraState {
    switch (event.type) {
        case "OPEN":
            return state.status === "closed" ? newSession(state, event.facing) : state
        case "STREAM_READY":
            if (event.session !== state.session || state.status !== "starting") return state
            return { ...state, status: "ready" }
        case "STREAM_FAILED":
            if (event.session !== state.session || state.status !== "starting") return state
            return { ...state, status: "failed", failure: event.failure }
        case "CAPTURED":
            return state.status === "ready" ? { ...state, status: "captured", photo: event.photo } : state
        case "RETAKE":
            return state.status === "captured" ? { ...state, status: "ready", photo: null } : state
        case "FLIP":
            if (state.status !== "ready") return state
            return newSession(state, state.facing === "user" ? "environment" : "user")
        case "RETRY":
            return state.status === "failed" ? newSession(state, state.facing) : state
        case "CLOSE":
            return state.status === "closed" ? state : { ...CLOSED_CAMERA, session: state.session }
        default:
            return state
    }
}

/** Le refus du navigateur, lu dans son erreur (`getUserMedia`). */
export function cameraFailureOf(error: unknown): CopyCameraFailure {
    const name = error instanceof Error || (typeof error === "object" && error != null) ? (error as Error).name : ""
    if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "denied"
    if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
        return "unavailable"
    }
    // La caméra existe mais ne démarre pas : une autre application la tient (« Lien avec Windows »,
    // Teams…) — `NotReadableError` sous Chrome et Edge, `AbortError` sous Firefox —, et chaque nouvelle
    // demande échoue pareil tant qu'elle ne la rend pas.
    if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") return "busy"
    return "error"
}
